/* eslint-disable @typescript-eslint/naming-convention */
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'

const STORAGE_KEY = 'timezoneOverride'

export function getSystemTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
}

function readStoredTimezone(): string | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)

        return raw && raw.length > 0 ? raw : null
    } catch {
        return null
    }
}

function persistTimezone(iana: string, systemTimezone: string) {
    try {
        if (iana === systemTimezone) {
            localStorage.removeItem(STORAGE_KEY)
        } else {
            localStorage.setItem(STORAGE_KEY, iana)
        }
    } catch {
        // ignore quota / private mode
    }
}

type TimezoneContextValue = {
    timezone: string
    systemTimezone: string
    isOverridden: boolean
    updateTimezone: (iana: string) => void
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null)

TimezoneContext.displayName = 'TimezoneContext'

function resolveInitialTimezone(): string {
    const systemTimezone = getSystemTimezone()
    const stored = readStoredTimezone()
    if (!stored) {
        return systemTimezone
    }

    if (stored === systemTimezone) {
        persistTimezone(stored, systemTimezone)

        return systemTimezone
    }

    return stored
}

export function TimezoneProvider({ children }: PropsWithChildren) {
    const [timezone, setTimezoneState] = useState(resolveInitialTimezone)

    const systemTimezone = useMemo(() => getSystemTimezone(), [])

    const isOverridden = timezone !== systemTimezone

    const updateTimezone = useCallback(
        (iana: string) => {
            const nextSystem = getSystemTimezone()
            setTimezoneState(iana === nextSystem ? nextSystem : iana)

            persistTimezone(iana, nextSystem)
        },
        [setTimezoneState],
    )

    const value = useMemo(
        () => ({
            timezone,
            systemTimezone,
            isOverridden,
            updateTimezone,
        }),
        [timezone, systemTimezone, isOverridden, updateTimezone],
    )

    return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>
}

export function useTimezone(): TimezoneContextValue {
    const context = useContext(TimezoneContext)
    if (!context) {
        throw new Error('useTimezone must be used within a TimezoneProvider')
    }

    return context
}
